import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  TextField,
  Pagination,
  Divider,
  Tab,
  Tabs,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useAuth } from '../service/AuthContext';
import { db } from '../service/Firebase-config';
import { collection, onSnapshot, orderBy, query, addDoc, updateDoc, deleteDoc, doc } from '@firebase/firestore';
import Parser from 'html-react-parser';
import usePagination from '../service/Pagination';
import DeleteConfirmation from '../common/DeleteConfirmation';

export default function DialogAnnouncement({ open, handleClose, isAdmin = false }) {
  const { userInfo } = useAuth();
  const [value, setValue] = useState(0);
  const [announcements, setAnnouncements] = useState([]);
  const [page, setPage] = useState(1);
  const [announcementTitle, setAnnouncementTitle] = useState('');
  const [announcementContent, setAnnouncementContent] = useState('');
  const [selectedRow, setSelectedRow] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [openDeletion, setOpenDeletion] = useState(false);
  const rowsPerPage = 10;
  const count = Math.ceil(announcements.length / rowsPerPage);
  const _DATA = usePagination(announcements, rowsPerPage, open);
  const dbCollection = collection(db, 'announcement');
  const [rows, setRows] = useState(15); 

  // Adjust rows based on the window height
  useEffect(() => {
    const updateRows = () => {
      const windowHeight = window.innerHeight;
      const lineHeight = 24;
      const availableHeight = windowHeight * 0.2; 
      const calculatedRows = windowHeight> 700 ? 15 : Math.floor(availableHeight / lineHeight);

      setRows(calculatedRows);
    };

    window.addEventListener('resize', updateRows);
    updateRows(); // Initial call to set the rows based on the initial window size

    return () => {
      window.removeEventListener('resize', updateRows);
    };
  }, []);

  useEffect(() => {
    if (open) {
      const fetchData = async () => {
        const orderedQuery = query(dbCollection, orderBy('date', 'desc'));
        const unsubscribe = onSnapshot(orderedQuery, (snapshot) => {
          const docs = snapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id }));
          setAnnouncements(docs);
          if (docs.length > 0) {
            setExpanded(docs[0].id);
          }
        });
        return () => unsubscribe(); // Cleanup subscription on unmount
      };
      fetchData();
    } else {
      clear();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const clear = () => {
    _DATA.jump(1);
    setPage(1);
    setAnnouncementContent('');
    setAnnouncementTitle('');
    setIsEditMode(false);
    setSelectedRow(null);
    setValue(0);
  };

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const handleQuery = (event) => {
    event.preventDefault();
    _DATA.search(event.target.value);
  };

  const handlePagination = (e, p) => {
    setPage(p);
    _DATA.jump(p);
  };

  const handleEditMode = (row) => {
    setValue(1);
    setIsEditMode(true);
    setSelectedRow(row);
    setAnnouncementContent(row.content);
    setAnnouncementTitle(row.title);
  };

  const saveAnnouncement = async (event) => {
    event.preventDefault();
    const updatedData = {
      date: new Date(),
      title: announcementTitle,
      content: announcementContent,
    };

    if (!isEditMode) {
      await addDoc(dbCollection, updatedData).then(() => clear());
    } else {
      const selectedRowRef = doc(db, 'announcement', selectedRow.id);
      await updateDoc(selectedRowRef, updatedData).then(() => clear());
    }
  };

  const handleDeleteRow = async () => {
    const selectedRowRef = doc(db, 'announcement', selectedRow.id);
    await deleteDoc(selectedRowRef).then(() => {
      clear();
      setOpenDeletion(false);
    });
  };

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      scroll="paper"
      PaperProps={{
        sx: {
          width: '60%',
          maxWidth: 'none',
          minHeight: 300,
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Announcement
          </Typography>
          {isAdmin && (
            <TextField
              label="Search Announcement"
              size="small"
              variant="outlined"
              onChange={handleQuery}
              sx={{ ml: 2 }}
            />
          )}
        </Box>
      </DialogTitle>
      <DialogContent>
        {isAdmin && (
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={value} onChange={handleChange} aria-label="announcement tabs">
              <Tab label="List" value={0} disabled={isEditMode} />
              {userInfo?.role === 'admin' && (
                <Tab label={isEditMode ? 'Edit Announcement' : 'Create new announcement'} value={1} />
              )}
            </Tabs>
          </Box>
        )}
        {!isAdmin && (
          <Box sx={{ display: 'flex', alignItems: 'flex-end', pt: 2, width: '100%' }}>
            <TextField label="Search Announcement" size="small" variant="outlined" onChange={handleQuery} />
          </Box>
        )}
        {value === 0 && (
          <Box sx={{ pt: 2 }}>
            {_DATA.currentData()?.map((row) => (
              <Box key={row.id}>
                <Accordion
                  expanded={expanded === row.id}
                  onChange={handleAccordionChange(row.id)}
                  sx={{ borderBottom: '1px solid lightblue' }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
                    aria-controls="panel1-content"
                    id="panel1-header"
                    sx={{ bgcolor: 'rgb(25, 118, 210)', display: 'flex', alignItems: 'center' }}
                  >
                    <Box sx={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                      <Typography sx={{ width: '15%', flexShrink: 0, color: 'white', fontWeight: 'bold' }}>
                        {new Date(row.date.seconds * 1000).toLocaleDateString("en-US", { month: 'short', day: '2-digit', year: 'numeric' })}
                      </Typography>
                      <Typography sx={{ width: '50%', flexShrink: 0, color: 'white', fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {row.title?.length < 60 ? row.title : `${row.title.substring(0, 60)}...`}
                      </Typography>
                      {userInfo?.role === 'admin' && (
                        <Box sx={{ ml: 'auto', display: 'flex' }}>
                          <Button
                            sx={{ color: 'white', marginLeft: '10px' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditMode(row);
                            }}
                          >
                            Edit
                          </Button>
                          <Button
                            sx={{ color: 'white' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedRow(row);
                              setOpenDeletion(true);
                            }}
                          >
                            Delete
                          </Button>
                        </Box>
                      )}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>{Parser(row.content)}</AccordionDetails>
                </Accordion>
              </Box>
            ))}
            <Box sx={{ display: 'flex', alignItems: 'flex-end', pt: 2, width: '100%' }}>
              <Pagination count={count} page={page} onChange={handlePagination} />
            </Box>
          </Box>
        )}
        {value === 1 && (
          <Box display="grid" gridTemplateColumns="repeat(12, 1fr)" gap={2}>
          <Box gridColumn="span 6">
            <TextField
              autoFocus
              required
              margin="dense"
              id="title"
              name="title"
              label="Title"
              fullWidth
              variant="outlined"
              value={announcementTitle}
              onChange={(e) => setAnnouncementTitle(e.target.value)}
            />
            <TextField
              required
              margin="dense"
              id="content"
              name="content"
              label="Content"
              multiline
              fullWidth
              variant="outlined"
              value={announcementContent}
              onChange={(e) => setAnnouncementContent(e.target.value)}
              rows={rows}
            />
          </Box>
          <Box gridColumn="span 6" sx={{ mt: 1, mb: 1, p: 2, border: '1px solid lightgrey', overflow: 'auto', height: 'auto' }}>
            <Typography variant="h5">Announcement HTML Previewer</Typography>
            <Divider sx={{ mt: 1, mb: 1 }} />
            {Parser(announcementContent)}
          </Box>
        </Box>
        )}
      </DialogContent>
      <DialogActions>
        {value === 1 && (
          <Button onClick={saveAnnouncement}>Submit</Button>
        )}
        <Button onClick={(e) => {
          e.stopPropagation();
          value === 1 ? clear() : handleClose();
        }}>
          {value === 1 ? 'Cancel' : 'Close'}
        </Button>
      </DialogActions>
      <DeleteConfirmation
        open={openDeletion}
        closeDelete={() => setOpenDeletion(false)}
        handleDelete={handleDeleteRow}
        title={`Are you sure you want to delete this announcement?`}
      />
    </Dialog>
  );
}
